import { useState } from 'react';
import {
  Button,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { useAccount } from '../../../contexts/Account';
import ACSnackbar from '../../ACSnackbar';
import { AccountType } from '../../../services/utils/types';

const useStyles = () => ({
  textHeadline: {
    fontWeight: '600',
    fontSize: '20px',
    padding: '8px 10px',
    color: '#4D586F',
  },
  textContent: {
    fontWeight: '400',
    fontSize: '14px',
    padding: '8px 10px',
    color: '#001030',
    padding: '0px',
    mb: '24px',
  },
  dialogActions: {
    width: '100%',
    marginTop: '8px',
    padding: '8px 12px',
  },
  mainButton: {
    width: '100%',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    height: '45px',
    borderRadius: '12px',
  },
});

export default function CopyModalContent(props) {
  const { modalType, forceSnackbar } = props;
  const { accountDetails, type } = useAccount();
  const joinUrl = `https://app.advisorycloud.com/join/advisors/${accountDetails.slug}/${accountDetails.id}`;
  const advisorUrl = `${window.location.origin}/profile/${accountDetails.publicProfileUrl}`;
  const imageName =
    type === AccountType.COMPANY ? 'JoinMyBoardButton' : 'ConnectWithMe';
  const embedCode = `
      <a href="${
        type === AccountType.COMPANY ? joinUrl : advisorUrl
      }" target="_blank" rel="noopener noreferrer">
        <!-- You can update the value of "width" to change the size of the button -->
        <img
          src="https://app.advisorycloud.com/images/${imageName}.png"
          alt="${
            type === AccountType.COMPANY
              ? 'Join My Board Button'
              : 'Connect With Me Button'
          }"
          style="
            display: block;
            width: 190px;
            cursor: pointer;
          "
        />
      </a>
      `;
  const customData = {
    facebook: {
      title: 'Facebook',
      icon: '/icons/facebook_logo.svg',
      text: {
        [AccountType.COMPANY]:
          'Click the button below to copy the announcement and paste it into a new post on Facebook.',
        [AccountType.ADVISOR]:
          'Click the button below to copy the announcement and paste it into a new post on Facebook.',
      },
      input: {
        [AccountType.COMPANY]: `I've built an Advisory Board on AdvisoryCloud, and would love for you to join!\n${joinUrl}`,
        [AccountType.ADVISOR]: `I'm now available as an advisor on AdvisoryCloud! Check it out and connect with me here:\n${advisorUrl}`,
      },
    },
    x: {
      title: 'X',
      icon: '/icons/x_logo.svg',
      text: {
        [AccountType.COMPANY]:
          'Click the button below to copy the announcement and paste it into a new post on X.',
        [AccountType.ADVISOR]:
          'Click the button below to copy the announcement and paste it into a new post on X.',
      },
      input: {
        [AccountType.COMPANY]: `I've built an Advisory Board on AdvisoryCloud, and would love for you to join!\n${joinUrl}`,
        [AccountType.ADVISOR]: `I'm now available as an advisor on AdvisoryCloud! Check it out and connect with me here:\n${advisorUrl}`,
      },
    },
    code: {
      title: 'your website',
      icon: '/icons/web_logo.svg',
      text: {
        [AccountType.COMPANY]:
          "We've made it easy to share your advisory board through your website, so your priority visitors and customers can join!",
        [AccountType.ADVISOR]:
          "We've made it easy to share your AdvisoryCloud Profile on your website or email signature so colleagues, customers, and business contacts can work with you.",
      },
      input: {
        [AccountType.COMPANY]: embedCode,
        [AccountType.ADVISOR]: embedCode,
      },
    },
  };
  const classes = useStyles();
  const [inputValue, setInputValue] = useState(
    customData[modalType]?.input[type]
  );
  const [successMessage, setSuccessMessage] = useState('');
  const [successSnackbar, setSuccessSnackbar] = useState(false);

  return (
    <>
      <DialogTitle
        id="alert-dialog-title"
        display="flex"
        sx={{ ...classes.textHeadline, padding: '12px 12px' }}
      >
        <img
          src={customData[modalType]?.icon}
          alt={`${customData[modalType]?.title} Logo`}
          style={{ marginRight: '12px' }}
        />
        Share on {customData[modalType]?.title}
      </DialogTitle>
      <DialogContent sx={{ padding: '8px 12px' }}>
        <Typography sx={classes.textContent}>
          {customData[modalType]?.text[type]}
        </Typography>
        {modalType === 'code' ? (
          <Box display="flex">
            <img
              src={`images/${imageName}.png`}
              alt={`${imageName} Button`}
              style={{
                display: 'block',
                width: '170px',
                height: type === AccountType.COMPANY ? '46px' : '38px',
                cursor: 'pointer',
                marginRight: '10px',
              }}
            />
            {type === AccountType.COMPANY ? (
              <Typography sx={classes.textContent}>
                Click below to copy the code to add this button to your website.
              </Typography>
            ) : (
              <Typography sx={classes.textContent}>
                Click below to copy the code for the image, so you can paste it
                on your site or signature.
              </Typography>
            )}
          </Box>
        ) : (
          <TextField
            id="advisorHeadline"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={inputValue}
            onChange={(evt) => setInputValue(evt.target.value)}
            inputProps={{
              maxLength: 200,
            }}
            InputProps={{
              style: {
                borderRadius: '8px',
              },
            }}
          />
        )}
      </DialogContent>
      <DialogActions sx={classes.dialogActions}>
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            navigator.clipboard.writeText(inputValue);
            setSuccessMessage(
              `${
                modalType === 'code' ? 'Code snippet' : 'Text'
              } copied, you can now share it with whoever you choose.`
            );
            setSuccessSnackbar(true);
          }}
          sx={{
            ...classes.mainButton,
            color: '#ffffff',
            backgroundColor: '#2B71F6',
            '&:hover': {
              backgroundColor: '#2B71F6',
            },
          }}
        >
          Copy{modalType === 'code' ? ' Code <>' : ''}
        </Button>
      </DialogActions>
      <ACSnackbar
        open={successSnackbar}
        text={successMessage}
        severity="success"
        onClose={() => {
          setSuccessSnackbar(false);
        }}
        autoHideDuration={6000}
        sx={
          forceSnackbar
            ? {
                transform: 'translateX(-0%) !important',
              }
            : {}
        }
      />
    </>
  );
}
